import type { Placement } from '@floating-ui/react';
import {
  autoUpdate,
  flip,
  FloatingPortal,
  offset,
  shift,
  useDismiss,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
  useMergeRefs,
  useRole,
  useTransitionStyles,
} from '@floating-ui/react';
import { cloneElement, isValidElement, ReactNode, useState } from 'react';

import { cn } from '@/utils/classname';

interface TooltipProps {
  title: ReactNode | string;
  children: ReactNode;
  placement?: Placement;
  defaultOpen?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  delay?: number;
  tooltipClassName?: string;
}

const animationDuration = 250;

export function Tooltip(props: TooltipProps) {
  const {
    placement = 'bottom',
    children,
    title,
    open: controlledOpen,
    onOpenChange: setControlledOpen,
    defaultOpen,
    delay = 100,
    tooltipClassName,
  } = props;

  // Standard controlled/uncontrolled pattern - replaces useStorage to fix FloatingUI circular dependency
  const [internalOpen, setInternalOpen] = useState(defaultOpen ?? false);
  const open = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const setOpen = (value: boolean) => {
    setInternalOpen(value);
    setControlledOpen?.(value);
  };

  const data = useFloating({
    placement,
    open,
    onOpenChange: setOpen,
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(5),
      flip({
        crossAxis: placement.includes('-'),
        fallbackAxisSideDirection: 'start',
        padding: 5,
      }),
      shift({ padding: 5 }),
    ],
  });

  const context = data.context;

  const { isMounted, styles } = useTransitionStyles(context, {
    duration: animationDuration,
    initial: {
      opacity: 0,
    },
  });

  const hover = useHover(context, {
    move: false,
    delay,
  });
  const focus = useFocus(context, {});
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: 'tooltip' });

  const interactions = useInteractions([hover, focus, dismiss, role]);

  const validElement = isValidElement(children);
  // React 19 compatible ref access
  const childrenRef =
    validElement && typeof children === 'object' && 'props' in children
      ? (children.props as any).ref
      : null;

  const triggerRef = useMergeRefs([context.refs.setReference, childrenRef]);
  const tooltipRef = useMergeRefs([context.refs.setFloating]);

  return (
    <>
      {validElement &&
        cloneElement(
          children,
          interactions.getReferenceProps({
            ref: triggerRef,
            'data-state': context.open ? 'open' : 'closed',
            'data-testid': 'tooltip-trigger',
            'data-tooltip-title': title,
            ...(children as any)?.props,
          })
        )}
      {!validElement && (
        <div
          ref={triggerRef}
          // The user can style the trigger based on the state
          data-state={context.open ? 'open' : 'closed'}
          {...interactions.getReferenceProps()}
        >
          {children}
        </div>
      )}
      {title && isMounted && (
        <FloatingPortal>
          <div
            ref={tooltipRef}
            data-testid="tooltip-content"
            className={cn(
              // Shape - rounded corners for modern look
              'rounded-md',
              // Background - design system colors with dark theme support
              'bg-chariot-background-primary',
              '[data-theme="dark"]:bg-opacity-95 [data-theme="dark"]:backdrop-blur-sm',
              // Border - subtle definition
              'border-chariot-stroke border',
              // Padding - more breathing room
              'p-3',
              // Typography - design system text colors
              'text-chariot-text-primary text-xs',
              // Elevation - enhanced shadow for depth
              'ring-opacity-5 shadow-xl ring-1 ring-black',
              tooltipClassName
            )}
            style={{
              ...context.floatingStyles,
              ...styles,
              zIndex: 9999, // High z-index for table visibility
            }}
            {...interactions.getFloatingProps()}
          >
            {title}
          </div>
        </FloatingPortal>
      )}
    </>
  );
}
